import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from "src/services";
import { IBreed } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../Button/Button";
import Dropdown, { IOption } from "../Dropdown/Dropdown";

import "./JudgeBooksFilterModal.scss";

interface IProps {
  onDownload: (filterData: {
    breed_filter_type: 
      | typeof Constants.breed_filter_type.all 
      | typeof Constants.breed_filter_type.breeds 
      | typeof Constants.breed_filter_type.breed_groups;
    breed_filters?: string[];
    breed_group_filters?: string[];
  }) => void;
  onCancel: () => void;
  preloadedBreeds?: IBreed[]; // Optional preloaded breeds data
  eventId: string;
  showId: string;
  showElement?: string; // Current element filter
  showLevel?: string; // Current level filter
  showSection?: string; // Current section filter
}

interface IFilterItem {
  name: string;
  type: 'breed' | 'breed_group';
  count: number;
}

const JudgeBooksFilterModal: React.FC<IProps> = (props) => {
  const [filterType, setFilterType] = useState<
    typeof Constants.breed_filter_type.all | 
    typeof Constants.breed_filter_type.breeds | 
    typeof Constants.breed_filter_type.breed_groups
  >(Constants.breed_filter_type.all);
  const [selectedItems, setSelectedItems] = useState<IFilterItem[]>([]);
  const [availableItems, setAvailableItems] = useState<IFilterItem[]>([]);
  const [currentDropdownValue, setCurrentDropdownValue] = useState<IOption>({ id: '', value: '' });

  const loadAvailableItems = useCallback(async () => {
    try {
      // Build query filters to match the current show/element/level/section filters
      const queryFilters: any[] = [
        {
          attribute_name: "show_id",
          attribute_value: {
            operator: "$eq",
            value: props.showId,
          },
        },
      ];

      // Add element filter if specified
      if (props.showElement && props.showElement !== "None") {
        queryFilters.push({
          attribute_name: "dog_class.show_element",
          attribute_value: {
            operator: "$eq",
            value: props.showElement,
          },
        });
      }

      // Add level filter if specified
      if (props.showLevel && props.showLevel !== "None") {
        queryFilters.push({
          attribute_name: "dog_class.level",
          attribute_value: {
            operator: "$eq",
            value: props.showLevel,
          },
        });
      }

      // Add section filter if specified
      if (props.showSection && props.showSection !== "None") {
        queryFilters.push({
          attribute_name: "dog_class.section",
          attribute_value: {
            operator: "$eq",
            value: props.showSection,
          },
        });
      }

      // Get scores for this event and show with current filters
      const scores = await apiService.getEventScores(props.eventId, {
        query: {
          $and: queryFilters,
        },
      });

      // Count breeds
      const breedCounts = new Map<string, number>();
      const breedGroupCounts = new Map<string, number>();

      scores.forEach((score) => {
        if (score.breed) {
          breedCounts.set(score.breed, (breedCounts.get(score.breed) || 0) + 1);
        }
        if (score.breed_group) {
          breedGroupCounts.set(score.breed_group, (breedGroupCounts.get(score.breed_group) || 0) + 1);
        }
      });

      // Create available items array
      const items: IFilterItem[] = [];
      
      // Add breeds with counts
      Array.from(breedCounts.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([breed, count]) => {
          items.push({ name: breed, type: 'breed', count });
        });

      // Add breed groups with counts
      Array.from(breedGroupCounts.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([breedGroup, count]) => {
          items.push({ name: breedGroup, type: 'breed_group', count });
        });

      setAvailableItems(items);
    } catch (error) {
      console.error('Failed to load available items:', error);
    }
  }, [props.eventId, props.showId, props.showElement, props.showLevel, props.showSection]);

  useEffect(() => {
    // Load available items with counts from actual event scores
    loadAvailableItems();
  }, [loadAvailableItems]);

  // Get dropdown options for the current filter type
  const getDropdownOptions = () => {
    const currentType = filterType === Constants.breed_filter_type.breeds ? 'breed' : 'breed_group';
    
    return availableItems
      .filter(item => 
        item.type === currentType &&
        !selectedItems.some(selected => selected.name === item.name && selected.type === currentType)
      )
      .map(item => ({
        id: item.name,
        value: `${item.name} (${item.count} entries)`
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  };

  const handleFilterTypeChange = (type: typeof Constants.breed_filter_type.all | typeof Constants.breed_filter_type.breeds | typeof Constants.breed_filter_type.breed_groups) => {
    setFilterType(type);
    setSelectedItems([]);
    setCurrentDropdownValue({ id: '', value: '' });
  };

  const handleDropdownChange = (e: any, option: IOption) => {
    if (!option || !option.id || option.id === '') return;
    
    const type: 'breed' | 'breed_group' = filterType === Constants.breed_filter_type.breeds ? 'breed' : 'breed_group';
    const name = option.id;
    // Find the count for this item from available items
    const availableItem = availableItems.find(item => item.name === name && item.type === type);
    const count = availableItem ? availableItem.count : 0;
    
    setSelectedItems(prev => [...prev, { name, type, count }]);
    
    // Clear the dropdown to allow selecting another item
    setCurrentDropdownValue({ id: '', value: '' });
  };

  const removeSelectedItem = (index: number) => {
    setSelectedItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleDownload = () => {
    if (filterType === Constants.breed_filter_type.all) {
      props.onDownload({ breed_filter_type: Constants.breed_filter_type.all });
    } else if (filterType === Constants.breed_filter_type.breeds) {
      const breedNames = selectedItems.filter(item => item.type === 'breed').map(item => item.name);
      props.onDownload({
        breed_filter_type: Constants.breed_filter_type.breeds,
        breed_filters: breedNames
      });
    } else if (filterType === Constants.breed_filter_type.breed_groups) {
      const groupNames = selectedItems.filter(item => item.type === 'breed_group').map(item => item.name);
      props.onDownload({
        breed_filter_type: Constants.breed_filter_type.breed_groups,
        breed_group_filters: groupNames
      });
    }
  };

  const isDownloadDisabled = () => {
    if (filterType === Constants.breed_filter_type.all) return false;
    return selectedItems.length === 0;
  };

  return (
    <div className="judge-books-filter-modal">
      <h2>Download Judge's Books</h2>
      <p>Select which judge's books you want to generate:</p>
      
      <div className="scrollable-content">
        <div className="filter-options">
        <label className={`radio-option ${filterType === Constants.breed_filter_type.all ? 'selected' : ''}`}>
          <input
            type="radio"
            name="filterType"
            value={Constants.breed_filter_type.all}
            checked={filterType === Constants.breed_filter_type.all}
            onChange={() => handleFilterTypeChange(Constants.breed_filter_type.all)}
          />
          <div className="option-content">
            <div className="option-title">All Judge's Books</div>
          </div>
        </label>

        <label className={`radio-option ${filterType === Constants.breed_filter_type.breeds ? 'selected' : ''}`}>
          <input
            type="radio"
            name="filterType"
            value={Constants.breed_filter_type.breeds}
            checked={filterType === Constants.breed_filter_type.breeds}
            onChange={() => handleFilterTypeChange(Constants.breed_filter_type.breeds)}
          />
          <div className="option-content">
            <div className="option-title">Filter by Breeds</div>
          </div>
        </label>

        <label className={`radio-option ${filterType === Constants.breed_filter_type.breed_groups ? 'selected' : ''}`}>
          <input
            type="radio"
            name="filterType"
            value={Constants.breed_filter_type.breed_groups}
            checked={filterType === Constants.breed_filter_type.breed_groups}
            onChange={() => handleFilterTypeChange(Constants.breed_filter_type.breed_groups)}
          />
          <div className="option-content">
            <div className="option-title">Filter by Breed Groups</div>
          </div>
        </label>
        </div>

        {filterType !== Constants.breed_filter_type.all && (
          <div className="selection-section">
            <div className="section-title">
              {filterType === Constants.breed_filter_type.breeds ? 'Select Breeds' : 'Select Breed Groups'}
            </div>
            <div className="section-subtitle">
              Select multiple items from the dropdown below. Each selection will be added to your list.
            </div>
            <div className="dropdown-container">
              <Dropdown
                label={filterType === Constants.breed_filter_type.breeds ? 'Select Breeds' : 'Select Breed Groups'}
                placeholder={filterType === Constants.breed_filter_type.breeds ? 'Select breeds...' : 'Select breed groups...'}
                options={getDropdownOptions()}
                onChange={handleDropdownChange}
                value={currentDropdownValue}
              />
            </div>

            <div className="selected-items">
              <div className="selected-title">Selected {filterType === Constants.breed_filter_type.breeds ? 'Breeds' : 'Breed Groups'} ({selectedItems.length}):</div>
              {selectedItems.length > 0 ? (
                <div className="items-list">
                  {selectedItems.map((item, index) => (
                    <div key={index} className="selected-item">
                      <span>{item.name} ({item.count} dogs)</span>
                      <button 
                        className="remove-btn" 
                        onClick={() => removeSelectedItem(index)}
                        type="button"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-items">No items selected yet. Use the dropdown above to add items.</div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="modal-actions">
        <Button 
          className="secondary" 
          onClick={props.onCancel}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDownload}
          disabled={isDownloadDisabled()}
        >
          Download
        </Button>
      </div>
    </div>
  );
};

export default JudgeBooksFilterModal;